import { toUrl } from '@karakuri-web/common/utils'

import { SEO } from '../components'

export interface SharedSEOWidgetSetting {
  title?: string | null
  description?: string | null
  headerImageUrl?: string | null
  headerImageAltText?: string | null
  language?: string | null
  twitter?: string | null
  facebook?: string | null
  siteUrl?: string | null
  botId?: string | null
  pathPrefix?: string | null
}

interface Props {
  settingYaml: SharedSEOWidgetSetting
  title?: string
  description?: string
  banner?: string
  pathname?: string
  location?: { pathname: string }
  disableDefaultDescription?: boolean
}

function isResolvedPathname(pathname?: string): pathname is string {
  return typeof pathname === 'string' && pathname.length > 0 && !pathname.includes('*')
}

function withTrailingSlash(pathname: string): string {
  return pathname === '/' || pathname.endsWith('/') ? pathname : `${pathname}/`
}

function normalizeSeoPathname(pathname: string | undefined, pathPrefix: string | null | undefined) {
  if (!isResolvedPathname(pathname)) {
    return undefined
  }

  const normalizedPathname = pathname.startsWith('/') ? pathname : `/${pathname}`
  const normalizedPrefix = pathPrefix?.replace(/^\/|\/$/g, '') || ''

  if (!normalizedPrefix) {
    return withTrailingSlash(normalizedPathname)
  }

  if (normalizedPathname === `/${normalizedPrefix}`) {
    return '/'
  }

  if (normalizedPathname.startsWith(`/${normalizedPrefix}/`)) {
    return withTrailingSlash(normalizedPathname.slice(normalizedPrefix.length + 1))
  }

  return withTrailingSlash(normalizedPathname)
}

/**
 * FAQ / FAQプレビュー共通の SEO ウィジェット本体
 */
export function SharedSEOWidget({
  settingYaml,
  title,
  description,
  banner,
  pathname,
  location,
  disableDefaultDescription,
}: Props) {
  const {
    title: defaultTitle,
    description: defaultDescription,
    botId,
    siteUrl,
    pathPrefix,
    headerImageUrl: defaultBanner,
    headerImageAltText,
    language,
    twitter,
    facebook,
  } = settingYaml

  const seoPathname =
    normalizeSeoPathname(pathname, pathPrefix) ||
    normalizeSeoPathname(location?.pathname, pathPrefix)

  const fullUrl = toUrl(siteUrl, pathPrefix, botId, seoPathname)

  return (
    <SEO
      title={title}
      description={description}
      banner={banner}
      defaultTitle={defaultTitle || ''}
      defaultDescription={defaultDescription || ''}
      url={fullUrl}
      defaultBanner={defaultBanner || ''}
      imageAlt={headerImageAltText || ''}
      language={language || ''}
      twitter={twitter || ''}
      facebook={facebook || ''}
      canonical={fullUrl}
      disableDefaultDescription={disableDefaultDescription}
    />
  )
}
